import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { passwordStrength } from 'check-password-strength';
import TextInput from '../../shared/FormComponents/TextInput';
import { useLocalization } from '../../custom-hooks/useLocalization';
import { withLocalization } from '../../shared/Context/LocalizationContext';
import AccountForm from '../../shared/UserAccountForm';
import { withServerStyles } from '../../shared/ServerStylesConditionalWrapper';
import { compose } from '../../utils/compose';

const ChangePasswordStyledWrapper = styled.div`
  .form-field {
    margin-top: 2rem;
  }

  .password-policy {
    margin-top: 2rem;

    h2 {
      font-size: 2rem;
    }
  }
`;

const passwordOptions = [
  {
    id: 0,
    value: 'not-acceptable',
    minDiversity: 0,
    minLength: 8
  },
  {
    id: 1,
    value: 'average',
    minDiversity: 3,
    minLength: 8
  },
  {
    id: 2,
    value: 'strong',
    minDiversity: 3,
    minLength: 12
  },
  {
    id: 3,
    value: 'excellent',
    minDiversity: 3,
    minLength: 18
  }
];

const DEFAULT_STATE = {
  oldPassword: '',
  password: '',
  passwordConfirm: '',
  passwordStrength: 0,
  passwordMismatch: false,
  disableSubmit: true
};

const ChangePasswordComponent = (props) => {
  const [state, setstate] = useState(DEFAULT_STATE);
  const setState = (newState) => setstate({ ...state, ...newState });
  const { resourceStrings } = useLocalization();
  const strengths = [
    resourceStrings['bdO.PasswordReset.PasswordStrength-not-acceptable'],
    resourceStrings['bdO.PasswordReset.PasswordStrength-average'],
    resourceStrings['bdO.PasswordReset.PasswordStrength-strong'],
    resourceStrings['bdO.PasswordReset.PasswordStrength-excellent']
  ];
  const recaptchaRef = useRef(null);
  const hiddenSubmitRef = useRef(null);

  const handleMatch = (event) => {
    // Compare password fields
    const comparePassword = event.target.name === 'password' ? 'passwordConfirm' : 'password';
    return {
      passwordMismatch: event.target.value !== state[comparePassword],
      [event.target.name]: event.target.value
    };
  };

  const handleInput = (event) => {
    let handlePassStrength = {};

    if (event.target.name === 'password')
      handlePassStrength = {
        passwordStrength: passwordStrength(event.target.value, passwordOptions).id
      };

    setState({ ...handlePassStrength, ...handleMatch(event) });
  };

  const getRecaptchaToken = async (e) => {
    e.preventDefault();
    const action = window.location.pathname === '/change-password' ? 'ChangePassword' : 'Reset';
    try {
      const token = await new Promise((resolve, reject) =>
        window.grecaptcha.enterprise.ready(() => {
          window.grecaptcha.enterprise
            .execute(props.recaptchaSiteKey, {
              action
            })
            .then(resolve)
            .catch(reject);
        })
      );
      recaptchaRef.current.value = token;
    } catch (err) {
      console.log('error', err);
    } finally {
      hiddenSubmitRef.current.closest('form').submit();
    }
  };

  const handleEnterKeySubmit = (e) => {
    if (e.submitter === hiddenSubmitRef.current) {
      e.preventDefault();
      e.stopPropagation();
      getRecaptchaToken(e);
    }
  };

  useEffect(() => {
    if (hiddenSubmitRef.current) {
      hiddenSubmitRef.current
        .closest('form')
        .addEventListener('submit', handleEnterKeySubmit, { once: true });
    }
  }, []);

  useEffect(() => {
    if (state.passwordMismatch) return setState({ disableSubmit: true });

    return setState({ disableSubmit: false });
  }, [state.oldPassword, state.passwordConfirm, state.password, state.passwordStrength]);

  return (
    <AccountForm useDefaultSubmitButton={false} heading={props.meta.pageTitle}>
      <ChangePasswordStyledWrapper>
        <div className="password-policy">
          <h2>{resourceStrings['bdO.PasswordPolicy-text']}</h2>
          <ul>
            {resourceStrings['bdO.PasswordPolicy-policies']?.split('-')?.map((policy) => (
              <li key={policy}>{policy}</li>
            ))}
          </ul>
          {props.errorMessage && (
            <p className="error-message">
              {props.errorMessage.split('\n').map((error) => (
                <>
                  {error} <br />
                </>
              ))}
            </p>
          )}
        </div>
        {props.isLoggedIn && (
          <div className="form-field">
            <TextInput
              id="oldPassword"
              name="oldPassword"
              label={resourceStrings['bdO.PasswordReset.OldPasswordLabel']}
              type="password"
              additionalAriaLabel="password-strength"
              onInput={handleInput}
              onBlur={handleInput}
              value={state.oldPassword}
              required
            />
          </div>
        )}
        <div className="form-field">
          <TextInput
            id="password"
            name="password"
            label={resourceStrings['bdO.PasswordReset.PasswordLabel']}
            type="password"
            errorMessages={{
              custom: state.passwordMismatch
                ? resourceStrings['bdO.PasswordPolicy.error.mismatch']
                : ''
            }}
            additionalAriaLabel="password-strength"
            onInput={handleInput}
            onBlur={handleInput}
            value={state.password}
            required
          />
        </div>
        {state.passwordStrength !== null && state.password + state.passwordConfirm !== '' && (
          <p aria-live="polite" id="password-strength">
            {resourceStrings['bdO.PasswordReset.PasswordStrength-text']}:{' '}
            <b>{strengths[state.passwordStrength]}</b>
          </p>
        )}
        <div className="form-field">
          <TextInput
            id="passwordConfirm"
            name="passwordConfirm"
            label={resourceStrings['bdO.PasswordReset.PasswordConfirmLabel']}
            type="password"
            errorMessages={{
              custom: state.passwordMismatch
                ? resourceStrings['bdO.PasswordPolicy.error.mismatch']
                : ''
            }}
            onInput={handleInput}
            value={state.passwordConfirm}
            required
          />
        </div>
        <div className="form-field">
          <input hidden id="GToken" name="GToken" type="text" ref={recaptchaRef} />
          <button
            data-sitekey="test"
            className={`btn btn-primary submit`}
            type="submit"
            onClick={getRecaptchaToken}
            disabled={state.disableSubmit}
          >
            {resourceStrings['bdO.PasswordReset.Confirm']}
          </button>
        </div>
        <button
          ref={hiddenSubmitRef}
          className="hidden btn btn-primary submit"
          type="submit"
        ></button>
      </ChangePasswordStyledWrapper>
    </AccountForm>
  );
};

export default compose(withLocalization, withServerStyles)(ChangePasswordComponent);
